// @ts-check
/**
 * CausalGraphService — Phase 0 local-only facade
 * Methods are intentionally minimal stubs; persistence uses Dexie when available.
 */

import Config from '../core/Config.js';
import Logger from '../utils/Logger.js';
import db from './db/DexieDB.js';
import { NodeKinds, EdgeKinds, isValidEdge, DEFAULTS, defaultPolarity } from './graph/types.js';

const log = Logger?.scope ? Logger.scope('Causality') : console;

/**
 * @typedef {Object} CausalEdge
 * @property {number} [id]
 * @property {string} fromExternalId
 * @property {string} toExternalId
 * @property {string} [type]            // influences | mitigates | enables | blocks
 * @property {('+'|'-'|1|-1)} [polarity]
 * @property {number} [strength]        // 0..1
 * @property {string} [evidenceLevel]   // anecdotal | expert | measured
 * @property {number} [confidence]      // 0..1
 * @property {string} [lastValidated]   // ISO date
 * @property {string} [owner]
 * @property {string} [notes]
 */

class CausalGraphServiceClass {
  constructor() {
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;
    this.initialized = true;
    log.info?.('CausalGraphService initialized (Phase 0: local mode)');
  }

  // ---- Convenience finders (Phase 1 upsert helpers) ----
  async _findByTitle(kind, title) {
    const t = String(title || '').trim();
    if (!t) return null;
    try {
      switch (String(kind)) {
        case 'objective': {
          const rows = await db.table('objectives').toArray();
          return rows.find(r => String(r.title || '').trim().toLowerCase() === t.toLowerCase()) || null;
        }
        case 'key_result': {
          const rows = await db.table('key_results').toArray();
          return rows.find(r => String(r.title || '').trim().toLowerCase() === t.toLowerCase()) || null;
        }
        case 'deliverable': {
          const rows = await db.table('deliverables').toArray();
          return rows.find(r => String(r.title || '').trim().toLowerCase() === t.toLowerCase()) || null;
        }
        case 'stakeholder': {
          const rows = await db.table('stakeholders').toArray();
          return rows.find(r => String(r.name || '').trim().toLowerCase() === t.toLowerCase()) || null;
        }
        default: return null;
      }
    } catch (_) { return null; }
  }

  async _upsertObjective(title, owner = '') {
    const existing = await this._findByTitle('objective', title);
    if (existing) return existing;
    return await this.createObjective({ title: String(title || 'Objective'), owner });
  }
  async _upsertKeyResult(title, objectiveId = '') {
    const existing = await this._findByTitle('key_result', title);
    if (existing) return existing;
    return await this.createKeyResult({ title: String(title || 'Key Result'), objectiveId });
  }
  async _upsertDeliverable(title, owner = '') {
    const existing = await this._findByTitle('deliverable', title);
    if (existing) return existing;
    return await this.createDeliverable({ title: String(title || 'Deliverable'), owner });
  }
  async _upsertStakeholder(name, meta = {}) {
    const existing = await this._findByTitle('stakeholder', name);
    if (existing) return existing;
    return await this.createStakeholder({ name: String(name || 'Stakeholder'), type: meta.type || 'user', segment: meta.segment || '', owner: meta.owner || '' });
  }

  /**
   * Create/merge Impact Mapping primitives for an initiative and wire edges.
   * Minimal heuristics: Objective from initiative.objective, Key Results from initiative.keyResults,
   * Deliverable from initiative.title, Stakeholders from initiative.stakeholders.
   * @param {{id:string,title?:string,objective?:string,keyResults?:string[],stakeholders?:string[],owner?:string}} initiative
   * @returns {Promise<{objective?:any, keyResults:any[], deliverable?:any, stakeholders:any[]}>}
   */
  async upsertImpactMapForInitiative(initiative) {
    await this.initialize();
    const result = { objective: null, keyResults: [], deliverable: null, stakeholders: [] };
    try {
      const owner = String(initiative?.owner || '');
      // Objective
      const objTitle = String(initiative?.objective || '').trim();
      let obj = null;
      if (objTitle) {
        obj = await this._upsertObjective(objTitle, owner);
        result.objective = obj;
      }
      // Deliverable: prefer explicit field; fallback to initiative title
      const delivTitle = String(initiative?.deliverable || initiative?.title || '').trim();
      if (delivTitle) {
        result.deliverable = await this._upsertDeliverable(delivTitle, owner);
        // Link initiative -> deliverable
        try { await this.createEdgeTyped(String(initiative.id), String(result.deliverable.id), 'produces', { confidence: 0.6 }); } catch (_) {}
      }
      // Key Results
      const krs = Array.isArray(initiative?.keyResults) ? initiative.keyResults : [];
      for (const krTitleRaw of krs) {
        const krTitle = String(krTitleRaw || '').trim();
        if (!krTitle) continue;
        const kr = await this._upsertKeyResult(krTitle, obj?.id || '');
        result.keyResults.push(kr);
        // Wire KR relations
        if (obj?.id) {
          // Objective -> KR as 'drives' (matches EDGE_RULES)
          try { await this.createEdgeTyped(String(obj.id), String(kr.id), 'drives', { confidence: 0.6 }); } catch (_) {}
        }
        // Initiative influences KR
        try { await this.createEdgeTyped(String(initiative.id), String(kr.id), 'influences', { confidence: 0.5 }); } catch (_) {}
      }
      // Stakeholders
      const stakeholders = Array.isArray(initiative?.stakeholders) ? initiative.stakeholders : [];
      for (const s of stakeholders) {
        const st = await this._upsertStakeholder(s, { owner });
        result.stakeholders.push(st);
        // Optionally link Objective benefits Stakeholder via influences (no dedicated kind)
        if (obj?.id) {
          try { await this.createEdgeTyped(String(obj.id), String(st.id), 'influences', { confidence: 0.4 }); } catch (_) {}
        }
      }
    } catch (e) {
      log.warn?.('upsertImpactMapForInitiative failed', e);
    }
    return result;
  }
  /**
   * Load nodes from local DB mirrors (objectives, key_results, initiatives).
   * Phase 1: Support extended node types (stakeholder, behavior, deliverable)
   * Returns minimal node descriptors using externalId fallbacks.
   */
  async getNodes() {
    try {
      const [objectives, keyResults, initiatives, stakeholders, behaviors, deliverables] = await Promise.all([
        safeArray(db.table?.('objectives')?.toArray?.()),
        safeArray(db.table?.('key_results')?.toArray?.()),
        safeArray(db.table?.('initiatives')?.toArray?.()),
        safeArray(db.table?.('stakeholders')?.toArray?.()),
        safeArray(db.table?.('behaviors')?.toArray?.()),
        safeArray(db.table?.('deliverables')?.toArray?.())
      ]);
      
      const normalize = (list, kind) => (list || []).map((n) => {
        // Phase 1: Handle extended node types stored in initiatives table
        let actualKind = kind;
        if (kind === 'initiative' && n.type) {
          // Map Impact Map node types
          if (n.type === 'stakeholder' || n.type === 'behavior' || n.type === 'deliverable') {
            actualKind = n.type;
          }
        }
        
        const externalId = asExternalId(n);
        
        return {
          kind: actualKind,
          id: externalId,  // This is already the external ID
          title: n.title || n.Title || n.name || n.Name || n.objective || n.key || n.id || 'Untitled',
          raw: n
        };
      });
      
      const base = [
        ...normalize(objectives, NodeKinds.OBJECTIVE),
        ...normalize(keyResults, NodeKinds.KEY_RESULT),
        ...normalize(initiatives, NodeKinds.INITIATIVE)
      ];
      // Dedicated tables override legacy initiative.type mapping
      const extras = [
        ...normalize(stakeholders, NodeKinds.STAKEHOLDER),
        ...normalize(behaviors, NodeKinds.BEHAVIOR),
        ...normalize(deliverables, NodeKinds.DELIVERABLE)
      ];
      return base.concat(extras);
    } catch (e) {
      log.warn?.('getNodes() failed, returning empty', e);
      return [];
    }
  }

  /** @returns {Promise<CausalEdge[]>} */
  async getEdges() {
    try {
      return await db.table('causal_edges').toArray();
    } catch {
      return [];
    }
  }

  /** Returns combined graph */
  async getGraph() {
    const [nodes, edges] = await Promise.all([this.getNodes(), this.getEdges()]);
    return { nodes, edges };
  }

  /**
   * Lightweight search across entity tables for merge-in-place UI
   * @param {'objective'|'key_result'|'stakeholder'|'behavior'|'deliverable'|'initiative'} kind
   * @param {string} q
   * @returns {Promise<Array<{id:string,title:string,kind:string}>>}
   */
  async search(kind, q) {
    const needle = String(q || '').trim().toLowerCase();
    if (!needle) return [];
    const like = (s) => String(s || '').toLowerCase().includes(needle);
    const mapRows = (rows, k) => (rows || []).filter(r => like(r.title || r.name || r.objective || r.id))
      .slice(0, 20)
      .map(r => ({ id: String(r.id), title: String(r.title || r.name || r.objective || r.id), kind: k }));
    switch (kind) {
      case 'objective': return mapRows(await safeArray(db.table?.('objectives')?.toArray?.()), 'objective');
      case 'key_result': return mapRows(await safeArray(db.table?.('key_results')?.toArray?.()), 'key_result');
      case 'stakeholder': return mapRows(await safeArray(db.table?.('stakeholders')?.toArray?.()), 'stakeholder');
      case 'behavior': return mapRows(await safeArray(db.table?.('behaviors')?.toArray?.()), 'behavior');
      case 'deliverable': return mapRows(await safeArray(db.table?.('deliverables')?.toArray?.()), 'deliverable');
      case 'initiative': return mapRows(await safeArray(db.table?.('initiatives')?.toArray?.()), 'initiative');
      default: return [];
    }
  }

  /** @param {CausalEdge} payload */
  async createEdge(payload) {
    // Determine defaults and validate against node kinds when possible
    let type = payload.type || EdgeKinds.INFLUENCES;
    let polarity = payload.polarity || DEFAULTS.edge.polarity;
    try {
      const nodes = await this.getNodes();
      const map = new Map(nodes.map(n => [n.id, n]));
      const from = map.get(payload.fromExternalId);
      const to = map.get(payload.toExternalId);
      if (from && to) {
        if (!isValidEdge(type, from.kind, to.kind)) {
          log.warn?.('Edge kind not valid for kinds; keeping as influences', { type, from: from.kind, to: to.kind });
        }
        polarity = payload.polarity || defaultPolarity(type, from.kind, to.kind, to.title);
      }
    } catch (_) {}

    const edge = this._validateEdge({
      type,
      polarity,
      strength: (typeof payload.strength === 'number' ? payload.strength : DEFAULTS.edge.strength),
      evidenceLevel: payload.evidenceLevel || DEFAULTS.edge.evidenceLevel,
      confidence: (typeof payload.confidence === 'number' ? payload.confidence : DEFAULTS.edge.confidence),
      lastValidated: payload.lastValidated || new Date().toISOString(),
      ...payload
    });
    const id = await db.table('causal_edges').add(edge);
    return { ...edge, id };
  }

  async updateEdge(id, patch) {
    const existing = await db.table('causal_edges').get(id);
    if (!existing) throw new Error(`Edge not found: ${id}`);
    const updated = this._validateEdge({ ...existing, ...patch });
    await db.table('causal_edges').update(id, updated);
    return { ...updated, id };
  }

  async deleteEdge(id) {
    await db.table('causal_edges').delete(id);
    return true;
  }

  // ---- Node creators (local v1 stubs) ----
  /** @param {{name:string,type?:string,segment?:string,owner?:string}} data */
  async createStakeholder(data) {
    const row = {
      id: `stake_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      name: data?.name || 'Stakeholder',
      type: data?.type || 'user',
      segment: data?.segment || '',
      owner: data?.owner || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await db.table('stakeholders').add(row);
    return row;
  }

  /** @param {{title:string,owner?:string}} data */
  async createBehavior(data) {
    const row = {
      id: `beh_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      title: data?.title || 'Behavior',
      owner: data?.owner || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await db.table('behaviors').add(row);
    return row;
  }

  /** @param {{title:string, owner?:string, description?:string}} data */
  async createObjective(data) {
    const row = {
      id: `obj_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      title: data?.title || 'Objective',
      description: data?.description || '',
      owner: data?.owner || '',
      status: 'new',
      progress: 0,
      updatedAt: new Date().toISOString()
    };
    await db.table('objectives').add(row);
    return row;
  }

  /** @param {{title:string, objectiveId?:string}} data */
  async createKeyResult(data) {
    const row = {
      id: `kr_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      title: data?.title || 'Key Result',
      objectiveId: data?.objectiveId || '',
      updatedAt: new Date().toISOString()
    };
    await db.table('key_results').add(row);
    return row;
  }

  /** @param {{title:string, owner?:string}} data */
  async createDeliverable(data) {
    const row = {
      id: `del_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      title: data?.title || 'Deliverable',
      owner: data?.owner || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await db.table('deliverables').add(row);
    return row;
  }

  async createEdgeTyped(fromId, toId, type, extra = {}) {
    return this.createEdge({ fromExternalId: fromId, toExternalId: toId, type, ...extra });
  }

  // ----- Entity getters/updates for shared editor -----
  async getEntity(kind, id) {
    const k = String(kind);
    switch (k) {
      case 'objective': return await db.table('objectives').get(id);
      case 'key_result': return await db.table('key_results').get(id);
      case 'stakeholder': return await db.table('stakeholders').get(id);
      case 'behavior': return await db.table('behaviors').get(id);
      case 'deliverable': return await db.table('deliverables').get(id);
      case 'initiative': return await db.table('initiatives').get(id);
      default: return null;
    }
  }

  async updateEntity(kind, id, patch) {
    const k = String(kind);
    const now = new Date().toISOString();
    const p = { ...patch };
    if (k !== 'stakeholder') p.updatedAt = now; else p.updatedAt = now;
    switch (k) {
      case 'objective': return await db.table('objectives').update(id, p);
      case 'key_result': return await db.table('key_results').update(id, p);
      case 'stakeholder': return await db.table('stakeholders').update(id, p);
      case 'behavior': return await db.table('behaviors').update(id, p);
      case 'deliverable': return await db.table('deliverables').update(id, p);
      case 'initiative': return await db.table('initiatives').update(id, p);
      default: return 0;
    }
  }

  /**
   * Phase 1: Sidecar-ready API stubs
   * These methods provide the interface for future remote backend
   */
  async isRemoteEnabled() {
    return !!Config?.CAUSALITY_API_URL;
  }

  async callRemote(endpoint, method = 'GET', body = null) {
    if (!this.isRemoteEnabled()) {
      throw new Error('Remote API not configured');
    }
    
    const url = `${Config.CAUSALITY_API_URL}${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Config.API_KEY || ''}`
      }
    };
    
    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }
    
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      log.error?.('Remote API call failed', { endpoint, error: e });
      throw e;
    }
  }

  // Sidecar endpoints (Phase 1 stubs, Phase 2 implementation)
  async impactMapStep(stepData) {
    if (await this.isRemoteEnabled()) {
      return this.callRemote('/impact-map/step', 'POST', stepData);
    }
    // Local fallback handled by ImpactMapDrawer component
    return { success: true, local: true };
  }

  async getLevers() {
    if (await this.isRemoteEnabled()) {
      return this.callRemote('/levers', 'GET');
    }
    // Local fallback handled by LeverExplorer component
    return { levers: [], local: true };
  }

  async runCausalTest(edgeId, testType) {
    if (await this.isRemoteEnabled()) {
      return this.callRemote('/causal/test', 'POST', { edgeId, testType });
    }
    // Local fallback - no causal testing in Phase 1
    return { success: false, message: 'Causal testing requires sidecar', local: true };
  }

  async getEmbeddings(text) {
    if (await this.isRemoteEnabled()) {
      return this.callRemote('/embeddings', 'POST', { text });
    }
    // Local fallback - use simple tokenization
    return { embeddings: null, local: true };
  }

  /**
   * Local suggestions using simple token overlap.
   * Phase 2 will use embeddings from sidecar
   * Returns array of { to, score, kind, title }
   */
  async suggestLinksLocal(nodeOrId, { direction = 'out', limit = 5 } = {}) {
    const graph = await this.getGraph();
    const id = typeof nodeOrId === 'string' ? nodeOrId : (nodeOrId?.id || '');
    if (!id) return [];
    const me = graph.nodes.find(n => n.id === id);
    if (!me) return [];
    const meToks = tokenize(me.title || me.raw?.title || me.id);
    const existing = new Set(
      graph.edges
        .filter(e => e.fromExternalId === id)
        .map(e => `${e.fromExternalId}->${e.toExternalId}`)
    );
    const candidates = [];
    for (const n of graph.nodes) {
      if (n.id === id) continue;
      const key = direction === 'in' ? `${n.id}->${id}` : `${id}->${n.id}`;
      if (existing.has(key)) continue;
      const toks = tokenize(n.title || n.raw?.title || n.id);
      const score = jaccard(meToks, toks);
      if (score > 0) candidates.push({ id: n.id, kind: n.kind, title: n.title, score });
    }
    candidates.sort((a, b) => b.score - a.score);
    return candidates.slice(0, limit);
  }

  /** Analyze loops using advanced loop detector */
  async analyzeLoops(graph) {
    try {
      const { detectLoops, findInterconnectedLoops } = await import('./graph/LoopDetector.js');
      const loops = detectLoops(graph);
      const connections = findInterconnectedLoops(loops);
      return { 
        loops, 
        connections,
        summary: {
          total: loops.length,
          reinforcing: loops.filter(l => l.type === 'R').length,
          balancing: loops.filter(l => l.type === 'B').length
        }
      };
    } catch (e) {
      log.warn?.('Loop analysis failed', e);
      // Fallback to legacy if available
      try {
        const mod = await import('./graph/LoopAnalysis.js');
        return mod?.analyzeLoops ? mod.analyzeLoops(graph) : { loops: [] };
      } catch {
        return { loops: [] };
      }
    }
  }

  /** Run smell detectors */
  async detectSmells(graph) {
    const mod = await import('./graph/SmellDetectors.js');
    return mod?.detectSmells ? mod.detectSmells(graph) : [];
  }

  /** @param {CausalEdge} edge */
  _validateEdge(edge) {
    if (!edge || !edge.fromExternalId || !edge.toExternalId) {
      throw new Error('Invalid edge: fromExternalId and toExternalId are required');
    }
    const polarity = (edge.polarity === '-' || edge.polarity === -1) ? '-' : '+';
    return {
      ...edge,
      polarity,
      strength: clamp01(edge.strength),
      confidence: clamp01(edge.confidence)
    };
  }
}

function clamp01(n) {
  const v = Number.isFinite(n) ? n : 0;
  return Math.max(0, Math.min(1, v));
}

function asExternalId(n) {
  // Prefer externalId; fallback to id as string
  const id = n?.externalId || n?.id || '';
  return typeof id === 'string' ? id : String(id || '');
}

async function safeArray(p) {
  try { return await p; } catch { return []; }
}

function tokenize(text) {
  const s = String(text || '').toLowerCase();
  return new Set(s.split(/[^a-z0-9]+/i).filter(t => t.length > 1));
}

function jaccard(aSet, bSet) {
  if (!(aSet && bSet)) return 0;
  let inter = 0;
  for (const t of aSet) if (bSet.has(t)) inter++;
  const union = aSet.size + bSet.size - inter;
  if (union === 0) return 0;
  return inter / union;
}

const CausalGraphService = new CausalGraphServiceClass();
export default CausalGraphService;
